import { computed, ref, watch, type Ref } from "vue";
import { useTrpc } from "@app/vue/providers/index";
import type { Member, Role, RotaSettings } from "../../Team/types";
import type { Templates } from "../types";
import { localDate, shiftDate } from "../../shared/dates";
import { dayNumber, planWarnings, weekHoursByPerson, type Absence, type RotaWarning } from "../engine/rules";
import type { Shift } from "./types";

/** The Monday of the week containing `iso`. */
export const mondayOf = (iso: string) => {
  const d = new Date(`${iso}T12:00:00`);
  const dow = d.getDay() === 0 ? 7 : d.getDay();
  return shiftDate(iso, 1 - dow);
};

// Everything the schedule needs for one week, and the derived warnings,
// hours and coverage. Reloads when the week changes.
export function useRota(weekStart: Ref<string>) {
  const trpc = useTrpc();
  const loading = ref(true);
  const shifts = ref<Shift[]>([]);
  const members = ref<Member[]>([]);
  const roles = ref<Role[]>([]);
  const settings = ref<RotaSettings | null>(null);
  const templates = ref<Templates | null>(null);
  const absences = ref<Absence[]>([]);

  const days = computed(() => Array.from({ length: 7 }, (_, i) => shiftDate(weekStart.value, i)));
  const range = computed(() => ({ from: days.value[0], to: days.value[6] }));

  const loadStatic = async () => {
    const [team, r, s, tpl] = await Promise.all([
      trpc.rota.team.list.query(),
      trpc.rota.roles.list.query(),
      trpc.rota.settings.get.query(),
      trpc.rota.templates.list.query(),
    ]);
    members.value = team.members as Member[];
    roles.value = r as Role[];
    settings.value = s as RotaSettings;
    templates.value = tpl as Templates;
  };

  const loadShifts = async () => {
    // A day either side, so rest-time warnings see the neighbouring weeks.
    const around = { from: shiftDate(range.value.from, -1), to: shiftDate(range.value.to, 1) };
    const [s, off] = await Promise.all([trpc.rota.shifts.list.query(around), trpc.rota.timeOff.list.query(around)]);
    shifts.value = s as Shift[];
    absences.value = off.filter((o) => o.status === "approved");
  };

  const load = async () => {
    loading.value = true;
    try {
      await Promise.all([members.value.length ? Promise.resolve() : loadStatic(), loadShifts()]);
    } finally {
      loading.value = false;
    }
  };
  watch(weekStart, loadShifts);

  const inWeek = computed(() =>
    shifts.value.filter((s) => s.shift_date >= range.value.from && s.shift_date <= range.value.to),
  );
  const people = computed(() => members.value.filter((m) => m.active));
  const warnings = computed<Map<number, RotaWarning[]>>(() =>
    settings.value ? planWarnings(shifts.value, members.value, settings.value, absences.value, localDate()) : new Map(),
  );
  const hours = computed(() => weekHoursByPerson(shifts.value, weekStart.value));
  const coverage = computed(() => {
    const total = inWeek.value.length;
    const filled = inWeek.value.filter((s) => s.staff_id != null).length;
    const drafts = inWeek.value.filter((s) => s.status === "draft").length;
    const flagged = inWeek.value.filter((s) => (warnings.value.get(s.id) ?? []).some((w) => w.kind !== "not_published")).length;
    return { total, filled, drafts, flagged };
  });
  const isPast = (iso: string) => dayNumber(iso) < dayNumber(localDate());

  return { absences, loading, shifts, inWeek, members, people, roles, settings, templates, days, range, warnings, hours, coverage, load, loadShifts, isPast };
}
